import moment from 'moment';
import { getValidPropertiesFromObject } from '@/utils/model';
import * as actions from './bookingActions';
import { BookingData } from '@/interfaces';

export const initialState: BookingData = {
    adId: null,
    enabled: false,
    rentalEnabled: false,
    bookings: [],
    outdated: [],
    partners: [],
    showPartnersFormField: false,
    selected: null,
    bookingStatus: 'NOT_STARTED',
    newEmptyBooking: false,
};

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const ALLOWED_BOOKING_PROPERTIES = new Set([
    'bookingId',
    'bookingNotes',
    'email',
    'endDate',
    'name',
    'phoneNumber',
    'rentalId',
    'startDate',
]);

function createBooking(entry) {
    const updatedEntry = {
        startDate: entry.start,
        endDate: entry.end,
        bookingNotes: entry.notes,
        ...entry,
    };

    for (const propertyName of ['startDate', 'endDate']) {
        if (updatedEntry[propertyName]) {
            const date = moment(updatedEntry[propertyName]).set('hour', 15);

            updatedEntry[propertyName] = date.format(DATE_FORMAT);
        }
    }

    return getValidPropertiesFromObject(updatedEntry, ALLOWED_BOOKING_PROPERTIES);
}

function updateIfEntryExists(array, data) {
    return array.map((entry) => {
        if (parseInt(entry.bookingId) === data.bookingId) {
            entry.bookingNotes = data.bookingNotes;
            entry.email = data.email;
            entry.endDate = data.endDate;
            entry.name = data.name;
            entry.phoneNumber = data.phoneNumber;
            entry.startDate = data.startDate;
        }
        return entry;
    });
}

function sortByStartDate(bookings) {
    return bookings.sort((a, b) => (a.startDate > b.startDate ? 1 : -1));
}

export default function reducer(state: BookingData = initialState, action) {
    switch (action.type) {
        case actions.POPULATE_BOOKINGS_PENDING: {
            return { ...state, bookingStatus: 'PENDING' };
        }
        case actions.POPULATE_BOOKINGS_SUCCESS: {
            const bookings = action.data.bookings.map(createBooking);

            return { ...state, ...action.data, bookings, bookingStatus: 'OK' };
        }
        case actions.CREATE_BOOKING_SUCCESS: {
            const newBooking = createBooking({
                bookingId: Number(action.response.bookingId),
                rentalId: action.data.adId,
                startDate: action.data.start,
                endDate: action.data.end,
                name: action.data.name,
                phoneNumber: action.data.phone,
                email: action.data.email,
                bookingNotes: action.data.notes,
            });
            const bookings = [...state.bookings, newBooking];

            return { ...state, bookings: sortByStartDate(bookings), newEmptyBooking: false, selected: newBooking, bookingStatus: 'OK' };
        }
        case actions.UPDATE_BOOKING_SUCCESS: {
            const updatedBookings = updateIfEntryExists(state.bookings, createBooking(action.data));

            return {
                ...state,
                bookings: updatedBookings,
                outdated: updateIfEntryExists(state.outdated, action.data), // TODO REIS-5376 remove outdated
                newEmptyBooking: false,
                selected: updatedBookings.find((entry) => parseInt(entry.bookingId) === action.data.bookingId) ?? null,
                bookingStatus: 'OK',
            };
        }
        case actions.DELETE_BOOKING_SUCCESS: {
            return {
                ...state,
                bookings: state.bookings.filter((entry) => Number(entry.bookingId) !== action.data.bookingId),
                outdated: state.outdated.filter((entry) => Number(entry.bookingId) !== action.data.bookingId),
                newEmptyBooking: false,
                selected: null,
                bookingStatus: 'OK',
            };
        }

        case actions.UPDATE_CALENDAR_SUCCESS: {
            return {
                ...state,
                enabled: action.enabled,
                rentalEnabled: action.enabled, // TODO REIS-5376 remove
            };
        }
        case actions.FETCH_PARTNERS_SUCCESS: {
            return { ...state, partners: action.data, showPartnersFormField: action.data && action.data.length > 0 };
        }
        case actions.TOGGLE_SELECTED_BOOKING: {
            return { ...state, newEmptyBooking: false, selected: action.data ?? null };
        }

        case actions.TOGGLE_BOOKING_STATUS: {
            return { ...state, bookingStatus: action.data ?? 'NOT_STARTED' };
        }

        case actions.TOGGLE_NEW_BOOKING: {
            return { ...state, newEmptyBooking: action.data.newEmptyBooking ?? false, selected: action.data.selected ?? null };
        }

        case actions.POPULATE_BOOKINGS_FAILED:
        case actions.CREATE_BOOKING_FAILED: {
            return { ...state, bookingStatus: 'ERROR' };
        }
        case actions.DELETE_BOOKING_FAILED:
        case actions.UPDATE_BOOKING_FAILED: {
            return { ...state, bookingStatus: 'ERROR' };
        }

        default: {
            return state;
        }
    }
}
